import {
  ErrorResponse,
  PaginatedRequest,
  PaginatedResponse,
} from '@opositandopn/http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useAxios } from '../../../context/AxiosContext';
import { DifficultyLevel } from '../../questions/api/useMultipleChoiceQuestions';

// .NET DateOnly is serialized as "YYYY-MM-DD"; the Axios interceptor hydrates
// it back to a Date at midnight UTC. Outbound Date values must be formatted
// explicitly so JSON.stringify doesn't emit a full ISO timestamp.
function toDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// TimeSpan is serialized by System.Text.Json as "HH:MM:SS" (or
// "D.HH:MM:SS.FFFFFFF" for longer durations). It does not match the
// ISO/date-only regexes, so Axios leaves it as a string.
export type TimeSpanString = string;

// --- Hang ----------------------------------------------------------------

export interface HangAttemptViewModel {
  id: string;
  date: Date;
  time: TimeSpanString;
  points: number;
  note?: string;
}

export interface HangSessionSummaryViewModel {
  date: Date;
  time: TimeSpanString;
  points: number;
  attemptCount: number;
}

export type AddHangAttemptRequest = {
  date: Date;
  time: TimeSpanString;
  note?: string;
};

export const useAddHangAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: AddHangAttemptRequest) {
    const response = await instance.post(`/v2/physical-tests/hang/attempts`, {
      date: toDateOnly(request.date),
      time: request.time,
      note: request.note,
    });

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, AddHangAttemptRequest>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getHangAttemptsOnDate'] });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type UpdateHangAttemptRequest = {
  attemptId: string;
  time: TimeSpanString;
  note?: string;
};

export const useUpdateHangAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateHangAttemptRequest) {
    const response = await instance.patch(
      `/v2/physical-tests/hang/attempts/${request.attemptId}`,
      {
        time: request.time,
        note: request.note,
      },
    );

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, UpdateHangAttemptRequest>(
    {
      mutationFn,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['getHangAttemptsOnDate'] });
        queryClient.invalidateQueries({
          queryKey: ['getPhysicalTestsMetrics'],
        });
        queryClient.invalidateQueries({
          queryKey: ['evaluatePhysicalTests'],
        });
      },
    },
  );
};

export type DeleteHangAttemptRequest = {
  attemptId: string;
};

export const useDeleteHangAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: DeleteHangAttemptRequest) {
    const response = await instance.delete(
      `/v2/physical-tests/hang/attempts/${request.attemptId}`,
    );

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, DeleteHangAttemptRequest>(
    {
      mutationFn,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['getHangAttemptsOnDate'] });
        queryClient.invalidateQueries({
          queryKey: ['getPhysicalTestsMetrics'],
        });
        queryClient.invalidateQueries({
          queryKey: ['evaluatePhysicalTests'],
        });
      },
    },
  );
};

export type GetHangAttemptsOnDateRequest = {
  date: Date;
};

export const useGetHangAttemptsOnDate = (
  request: GetHangAttemptsOnDateRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();
  const date = toDateOnly(request.date);

  async function queryFn(): Promise<HangAttemptViewModel[]> {
    const response = await instance.get<HangAttemptViewModel[]>(
      `/v2/physical-tests/hang/attempts`,
      { params: { date } },
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getHangAttemptsOnDate', date],
    queryFn,
    enabled,
  });
};

// --- Obstacle course -----------------------------------------------------

export interface ObstacleCourseAttemptViewModel {
  id: string;
  date: Date;
  time: TimeSpanString;
  points: number;
  note?: string;
}

export interface ObstacleCourseSessionSummaryViewModel {
  date: Date;
  time: TimeSpanString;
  points: number;
  attemptCount: number;
}

export type AddObstacleCourseAttemptRequest = {
  date: Date;
  time: TimeSpanString;
  note?: string;
};

export const useAddObstacleCourseAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: AddObstacleCourseAttemptRequest) {
    const response = await instance.post(
      `/v2/physical-tests/obstacle-course/attempts`,
      {
        date: toDateOnly(request.date),
        time: request.time,
        note: request.note,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    AddObstacleCourseAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getObstacleCourseAttemptsOnDate'],
      });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type UpdateObstacleCourseAttemptRequest = {
  attemptId: string;
  time: TimeSpanString;
  note?: string;
};

export const useUpdateObstacleCourseAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateObstacleCourseAttemptRequest) {
    const response = await instance.patch(
      `/v2/physical-tests/obstacle-course/attempts/${request.attemptId}`,
      {
        time: request.time,
        note: request.note,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    UpdateObstacleCourseAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getObstacleCourseAttemptsOnDate'],
      });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type DeleteObstacleCourseAttemptRequest = {
  attemptId: string;
};

export const useDeleteObstacleCourseAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: DeleteObstacleCourseAttemptRequest) {
    const response = await instance.delete(
      `/v2/physical-tests/obstacle-course/attempts/${request.attemptId}`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    DeleteObstacleCourseAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getObstacleCourseAttemptsOnDate'],
      });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type GetObstacleCourseAttemptsOnDateRequest = {
  date: Date;
};

export const useGetObstacleCourseAttemptsOnDate = (
  request: GetObstacleCourseAttemptsOnDateRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();
  const date = toDateOnly(request.date);

  async function queryFn(): Promise<ObstacleCourseAttemptViewModel[]> {
    const response = await instance.get<ObstacleCourseAttemptViewModel[]>(
      `/v2/physical-tests/obstacle-course/attempts`,
      { params: { date } },
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getObstacleCourseAttemptsOnDate', date],
    queryFn,
    enabled,
  });
};

// --- Pull-ups ------------------------------------------------------------

export interface PullUpsAttemptViewModel {
  id: string;
  date: Date;
  reps: number;
  points: number;
  note?: string;
}

export interface PullUpsSessionSummaryViewModel {
  date: Date;
  reps: number;
  points: number;
  attemptCount: number;
}

export type AddPullUpsAttemptRequest = {
  date: Date;
  reps: number;
  note?: string;
};

export const useAddPullUpsAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: AddPullUpsAttemptRequest) {
    const response = await instance.post(
      `/v2/physical-tests/pull-ups/attempts`,
      {
        date: toDateOnly(request.date),
        reps: request.reps,
        note: request.note,
      },
    );

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, AddPullUpsAttemptRequest>(
    {
      mutationFn,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['getPullUpsAttemptsOnDate'],
        });
        queryClient.invalidateQueries({
          queryKey: ['getPhysicalTestsMetrics'],
        });
        queryClient.invalidateQueries({
          queryKey: ['evaluatePhysicalTests'],
        });
      },
    },
  );
};

export type UpdatePullUpsAttemptRequest = {
  attemptId: string;
  reps: number;
  note?: string;
};

export const useUpdatePullUpsAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdatePullUpsAttemptRequest) {
    const response = await instance.patch(
      `/v2/physical-tests/pull-ups/attempts/${request.attemptId}`,
      {
        reps: request.reps,
        note: request.note,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    UpdatePullUpsAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getPullUpsAttemptsOnDate'],
      });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type DeletePullUpsAttemptRequest = {
  attemptId: string;
};

export const useDeletePullUpsAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: DeletePullUpsAttemptRequest) {
    const response = await instance.delete(
      `/v2/physical-tests/pull-ups/attempts/${request.attemptId}`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    DeletePullUpsAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getPullUpsAttemptsOnDate'],
      });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type GetPullUpsAttemptsOnDateRequest = {
  date: Date;
};

export const useGetPullUpsAttemptsOnDate = (
  request: GetPullUpsAttemptsOnDateRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();
  const date = toDateOnly(request.date);

  async function queryFn(): Promise<PullUpsAttemptViewModel[]> {
    const response = await instance.get<PullUpsAttemptViewModel[]>(
      `/v2/physical-tests/pull-ups/attempts`,
      { params: { date } },
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getPullUpsAttemptsOnDate', date],
    queryFn,
    enabled,
  });
};

// --- Run -----------------------------------------------------------------

export interface RunAttemptViewModel {
  id: string;
  date: Date;
  distance: number;
  pace: TimeSpanString;
  time: TimeSpanString;
  points: number;
  note?: string;
}

export interface RunSessionSummaryViewModel {
  date: Date;
  pace: TimeSpanString;
  points: number;
  attemptCount: number;
}

export type AddRunAttemptRequest = {
  date: Date;
  distance: number;
  time: TimeSpanString;
  note?: string;
};

export const useAddRunAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: AddRunAttemptRequest) {
    const response = await instance.post(`/v2/physical-tests/run/attempts`, {
      date: toDateOnly(request.date),
      distance: request.distance,
      time: request.time,
      note: request.note,
    });

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, AddRunAttemptRequest>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getRunAttemptsOnDate'] });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type UpdateRunAttemptRequest = {
  attemptId: string;
  distance: number;
  time: TimeSpanString;
  note?: string;
};

export const useUpdateRunAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateRunAttemptRequest) {
    const response = await instance.patch(
      `/v2/physical-tests/run/attempts/${request.attemptId}`,
      {
        distance: request.distance,
        time: request.time,
        note: request.note,
      },
    );

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, UpdateRunAttemptRequest>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getRunAttemptsOnDate'] });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type DeleteRunAttemptRequest = {
  attemptId: string;
};

export const useDeleteRunAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: DeleteRunAttemptRequest) {
    const response = await instance.delete(
      `/v2/physical-tests/run/attempts/${request.attemptId}`,
    );

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, DeleteRunAttemptRequest>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getRunAttemptsOnDate'] });
      queryClient.invalidateQueries({ queryKey: ['getPhysicalTestsMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['evaluatePhysicalTests'] });
    },
  });
};

export type GetRunAttemptsOnDateRequest = {
  date: Date;
};

export const useGetRunAttemptsOnDate = (
  request: GetRunAttemptsOnDateRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();
  const date = toDateOnly(request.date);

  async function queryFn(): Promise<RunAttemptViewModel[]> {
    const response = await instance.get<RunAttemptViewModel[]>(
      `/v2/physical-tests/run/attempts`,
      { params: { date } },
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getRunAttemptsOnDate', date],
    queryFn,
    enabled,
  });
};

// --- Metrics -------------------------------------------------------------

export type GetPhysicalTestsMetricsRequest = {
  fromDate: Date;
};

export type GetPhysicalTestsMetricsQueryResult = {
  hang: HangSessionSummaryViewModel[];
  pullUps: PullUpsSessionSummaryViewModel[];
  run: RunSessionSummaryViewModel[];
  obstacleCourse: ObstacleCourseSessionSummaryViewModel[];
};

export const useGetPhysicalTestsMetrics = (
  request: GetPhysicalTestsMetricsRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();
  const fromDate = toDateOnly(request.fromDate);

  async function queryFn(): Promise<GetPhysicalTestsMetricsQueryResult> {
    const response = await instance.get<GetPhysicalTestsMetricsQueryResult>(
      `/v2/physical-tests/metrics`,
      { params: { fromDate } },
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getPhysicalTestsMetrics', fromDate],
    queryFn,
    enabled,
  });
};

// --- Evaluation ----------------------------------------------------------

export type EvaluatePhysicalTestsCommandResult = {
  evaluation: string;
};

const EVALUATE_PHYSICAL_TESTS_GC_TIME = 30 * 60 * 1000;

export const useEvaluatePhysicalTests = (enabled?: boolean) => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function queryFn(): Promise<EvaluatePhysicalTestsCommandResult> {
    const response = await instance.post<EvaluatePhysicalTestsCommandResult>(
      `/v2/physical-tests/evaluate`,
      {},
    );

    return response.data;
  }

  const query = useQuery({
    queryKey: ['evaluatePhysicalTests'],
    queryFn,
    enabled,
    staleTime: Infinity,
    gcTime: EVALUATE_PHYSICAL_TESTS_GC_TIME,
  });

  const evaluate = () =>
    queryClient.fetchQuery({
      queryKey: ['evaluatePhysicalTests'],
      queryFn,
      staleTime: Infinity,
      gcTime: EVALUATE_PHYSICAL_TESTS_GC_TIME,
    });

  return { ...query, evaluate };
};

// --- Training plans ------------------------------------------------------

export interface TrainingPlanPreviewViewModel {
  id: string;
  name: string;
  difficultyLevel: DifficultyLevel;
}

export interface TrainingPlanViewModel extends TrainingPlanPreviewViewModel {
  description: string;
}

export type CreateTrainingPlanRequest = {
  name: string;
  description: string;
  difficultyLevel: DifficultyLevel;
};

export type CreateTrainingPlanCommandResult = {
  trainingPlanId: string;
};

export const useCreateTrainingPlan = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: CreateTrainingPlanRequest) {
    const response = await instance.post<CreateTrainingPlanCommandResult>(
      `/v2/physical-tests/training-plans`,
      request,
    );

    return response.data;
  }

  return useMutation<
    CreateTrainingPlanCommandResult,
    AxiosError<ErrorResponse>,
    CreateTrainingPlanRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getTrainingPlans'] });
    },
  });
};

export type UpdateTrainingPlanRequest = {
  trainingPlanId: string;
  name: string;
  description: string;
  difficultyLevel: DifficultyLevel;
};

export const useUpdateTrainingPlan = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateTrainingPlanRequest) {
    const response = await instance.patch(
      `/v2/physical-tests/training-plans/${request.trainingPlanId}`,
      {
        name: request.name,
        description: request.description,
        difficultyLevel: request.difficultyLevel,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    UpdateTrainingPlanRequest
  >({
    mutationFn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['getTrainingPlans'] });
      queryClient.invalidateQueries({
        queryKey: ['getTrainingPlan', variables.trainingPlanId],
      });
    },
  });
};

export type DeleteTrainingPlanRequest = {
  trainingPlanId: string;
};

export const useDeleteTrainingPlan = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: DeleteTrainingPlanRequest) {
    const response = await instance.delete(
      `/v2/physical-tests/training-plans/${request.trainingPlanId}`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    DeleteTrainingPlanRequest
  >({
    mutationFn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['getTrainingPlans'] });
      queryClient.invalidateQueries({
        queryKey: ['getTrainingPlan', variables.trainingPlanId],
        refetchType: 'none',
      });
    },
  });
};

export const useGetTrainingPlan = (
  trainingPlanId: string,
  enabled?: boolean,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<TrainingPlanViewModel> {
    const response = await instance.get<TrainingPlanViewModel>(
      `/v2/physical-tests/training-plans/${trainingPlanId}`,
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getTrainingPlan', trainingPlanId],
    queryFn,
    enabled,
  });
};

export type GetTrainingPlansRequest = PaginatedRequest & {
  params?: {
    text?: string;
    difficultyLevels?: DifficultyLevel[];
  };
};

export const useGetTrainingPlans = (request: GetTrainingPlansRequest) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<
    PaginatedResponse<TrainingPlanPreviewViewModel>
  > {
    const response = await instance.get<
      PaginatedResponse<TrainingPlanPreviewViewModel>
    >(`/v2/physical-tests/training-plans`, {
      params: {
        fromTime: request.fromTime,
        toTime: request.toTime,
        pageNumber: request.pageNumber,
        pageSize: request.pageSize,
        sort: request.sort,
        text: request.params?.text,
        difficultyLevel: request.params?.difficultyLevels,
      },
      paramsSerializer: {
        indexes: null,
      },
    });

    return response.data;
  }

  return useQuery({
    queryKey: ['getTrainingPlans', request],
    queryFn,
  });
};
